import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Text, Spinner, Stat, StatLabel, StatNumber, StatGroup, Button, Flex, useBreakpointValue, Select
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { isEntrada, labelTipo, corTextoTipo } from '../utils/lancamentos';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

function formatarMoeda(valor: number) {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

function formatarDataBR(dataStr: string) {
  if (!dataStr) return '';
  const [ano, mes, dia] = dataStr.split('-');
  return `${dia}/${mes}/${ano}`;
}

function formatarDia(dataStr: string) {
  if (!dataStr) return '';
  return dataStr.split('-')[2];
}

const meses = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const ExtratoBanco: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [banco, setBanco] = useState<any>(null);
  const [lancamentos, setLancamentos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [bancos, setBancos] = useState<any[]>([]);
  const [bancoId, setBancoId] = useState<string | null>(null);
  const [mes, setMes] = useState<number>(new Date().getMonth());
  const [ano, setAno] = useState<number>(new Date().getFullYear());

  // Pega banco_id da query string
  const params = new URLSearchParams(location.search);
  const bancoIdQuery = params.get('banco_id');

  useEffect(() => {
    if (!user) return;
    const fetchBancos = async () => {
      try {
        // A lista já vem com saldo_atual calculado pelo servidor
        const data = await api.get('/api/bancos');
        setBancos(data);
        // Usa o banco da query string se válido, senão o primeiro
        if (!bancoId && data.length > 0) {
          const bancoDaQuery = bancoIdQuery && data.find((b: any) => b.id === bancoIdQuery);
          setBancoId(bancoDaQuery ? bancoIdQuery : data[0].id);
        }
      } catch {
        setBancos([]);
      }
    };
    fetchBancos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Banco selecionado (com saldo_atual) derivado da lista
  useEffect(() => {
    setBanco(bancos.find((b: any) => b.id === bancoId) || null);
  }, [bancos, bancoId]);

  useEffect(() => {
    if (!user || !bancoId) return;
    const fetchLancamentos = async () => {
      setLoading(true);
      try {
        setLancamentos(await api.get(`/api/lancamentos?banco_id=${bancoId}&mes=${mes}&ano=${ano}`));
      } catch {
        setLancamentos([]);
      }
      setLoading(false);
    };
    fetchLancamentos();
  }, [user, bancoId, mes, ano]);

  if (!user || loading) {
    return <Spinner />;
  }

  // Totais do mês selecionado (transferências contam como entrada/saída DO
  // BANCO); saldo atual considera TODOS os lançamentos
  const entradas = lancamentos.filter(l => isEntrada(l.tipo_lancamento)).reduce((acc, l) => acc + Number(l.valor), 0);
  const saidas = lancamentos.filter(l => !isEntrada(l.tipo_lancamento)).reduce((acc, l) => acc + Number(l.valor), 0);
  const saldoAtual = banco?.saldo_atual ?? 0;
  // Com data de referência, o "inicial" é o saldo conferido naquele dia
  const rotuloReferencia = banco?.saldo_data
    ? `Saldo em ${String(banco.saldo_data).slice(8, 10)}/${String(banco.saldo_data).slice(5, 7)}`
    : 'Saldo Inicial';

  return (
    <Box p={4} maxW="900px" mx="auto">
      <Flex align="center" justify="flex-start" mb={4} gap={2}>
        <Button onClick={() => navigate('/bancos')} colorScheme="teal" variant="outline" size="sm">Voltar</Button>
        <Heading size="lg" textAlign="center" flex={1}>Extrato - {banco?.nome || 'Banco'}</Heading>
      </Flex>
      {/* Filtros banco, mês, ano */}
      <Flex gap={4} mb={6} align="center" justify="center" direction={{ base: 'column', md: 'row' }}>
        <Box w={{ base: '100%', md: '220px' }}>
          <Select value={bancoId || ''} onChange={e => setBancoId(e.target.value)} w="100%">
            {bancos.map(b => (
              <option value={b.id} key={b.id}>{b.nome}</option>
            ))}
          </Select>
        </Box>
        <Flex gap={4} w={{ base: '100%', md: 'auto' }}>
          <Select value={mes} onChange={e => setMes(Number(e.target.value))} w="100%">
            {meses.map((m, idx) => (
              <option value={idx} key={m}>{m}</option>
            ))}
          </Select>
          <Select value={ano} onChange={e => setAno(Number(e.target.value))} w="100%">
            {[ano-1, ano, ano+1].map(a => (
              <option value={a} key={a}>{a}</option>
            ))}
          </Select>
        </Flex>
      </Flex>
      {isMobile ? (
        <Box mb={6}>
          <Swiper
            modules={[Pagination]}
            spaceBetween={12}
            slidesPerView={1}
            pagination={{ clickable: true }}
            style={{ width: '100%' }}
          >
            <SwiperSlide>
              <Box p={8} borderRadius="md" boxShadow="md" bg="gray.50" textAlign="center">
                <Text fontWeight="bold" mb={1}>Saldo Atual</Text>
                <Text fontSize="2xl" fontWeight="bold" color={saldoAtual < 0 ? 'red.500' : 'teal.600'}>R$ {formatarMoeda(saldoAtual)}</Text>
              </Box>
            </SwiperSlide>
            <SwiperSlide>
              <Box p={8} borderRadius="md" boxShadow="md" bg="gray.50" textAlign="center">
                <Text fontWeight="bold" mb={1}>{rotuloReferencia}</Text>
                <Text fontSize="2xl" fontWeight="bold" color="teal.600">R$ {formatarMoeda(banco?.saldo_inicial || 0)}</Text>
              </Box>
            </SwiperSlide>
            <SwiperSlide>
              <Box p={8} borderRadius="md" boxShadow="md" bg="gray.50" textAlign="center">
                <Text fontWeight="bold" mb={1}>Entradas</Text>
                <Text fontSize="2xl" fontWeight="bold" color="teal.600">R$ {formatarMoeda(entradas)}</Text>
              </Box>
            </SwiperSlide>
            <SwiperSlide>
              <Box p={8} borderRadius="md" boxShadow="md" bg="gray.50" textAlign="center">
                <Text fontWeight="bold" mb={1}>Saídas</Text>
                <Text fontSize="2xl" fontWeight="bold" color="red.500">R$ {formatarMoeda(saidas)}</Text>
              </Box>
            </SwiperSlide>
          </Swiper>
        </Box>
      ) : (
        <StatGroup mb={8}>
          <Stat>
            <StatLabel>Saldo Atual</StatLabel>
            <StatNumber color={saldoAtual < 0 ? 'red.500' : 'teal.600'}>R$ {formatarMoeda(saldoAtual)}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>{rotuloReferencia}</StatLabel>
            <StatNumber color="gray.600">R$ {formatarMoeda(banco?.saldo_inicial || 0)}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Entradas</StatLabel>
            <StatNumber color="teal.600">R$ {formatarMoeda(entradas)}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Saídas</StatLabel>
            <StatNumber color="red.500">R$ {formatarMoeda(saidas)}</StatNumber>
          </Stat>
        </StatGroup>
      )}
      <Heading size="md" mb={4}>Lançamentos</Heading>
      {lancamentos.length === 0 ? (
        <Text>Nenhum lançamento encontrado para este banco.</Text>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple" w="100%">
            <Thead>
              <Tr>
                <Th>Data</Th>
                <Th display={{ base: 'none', md: 'table-cell' }}>Tipo</Th>
                <Th>Categoria</Th>
                <Th isNumeric>Valor</Th>
                <Th display={{ base: 'none', md: 'table-cell' }}>Descrição</Th>
              </Tr>
            </Thead>
            <Tbody>
              {lancamentos.map(lanc => (
                <Tr key={lanc.id}>
                  <Td>{isMobile ? formatarDia(lanc.data) : formatarDataBR(lanc.data)}</Td>
                  <Td display={{ base: 'none', md: 'table-cell' }}>
                    <Text color={corTextoTipo(lanc.tipo_lancamento)} fontWeight="bold">
                      {labelTipo(lanc.tipo_lancamento)}
                    </Text>
                  </Td>
                  <Td>{lanc.categoria?.nome || '-'}</Td>
                  <Td isNumeric color={corTextoTipo(lanc.tipo_lancamento)}>
                    R$ {formatarMoeda(Number(lanc.valor))}
                  </Td>
                  <Td display={{ base: 'none', md: 'table-cell' }}>{lanc.descricao || '-'}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default ExtratoBanco; 